'use client'

import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import {
  formatDayMonth,
  formatPeriod,
  formatRangeWithYear,
  inclusiveDays,
  MONTH_NAMES,
  MONTH_NAMES_GENITIVE,
  pluralRu,
  type MarketMoneyRange,
} from '@/lib/market-money'
import { useMarketMoneyPayouts } from './market-money-data'
import {
  DEFAULT_PERIOD_PRESET,
  PERIOD_PRESET_IDS,
  PERIOD_PRESET_LABELS,
  daysInMonth,
  presetRange,
  shiftMonth,
  type AnalyticsPeriod,
  type PeriodPresetId,
} from '@/lib/analytics-period-state'

export type { AnalyticsPeriod, PeriodPresetId }

interface AnalyticsPeriodPickerProps {
  value: AnalyticsPeriod
  firstOrderDate: string | null
  today: string
  onChange: (next: AnalyticsPeriod) => void
}

const MONTH_SHORT = ['янв', 'февр', 'март', 'апр', 'май', 'июнь', 'июль', 'авг', 'сент', 'окт', 'нояб', 'дек'] as const
const WEEKDAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'] as const

/** The picker bound to the shared payout data, which carries the first synchronised order date. */
export function MarketMoneyPeriodPicker({ value, today, onChange }: { value: AnalyticsPeriod; today: string; onChange: (next: AnalyticsPeriod) => void }) {
  const { payouts } = useMarketMoneyPayouts()
  return <AnalyticsPeriodPicker value={value} firstOrderDate={payouts?.presets.paid?.from ?? null} today={today} onChange={onChange} />
}

export function presetLabel(preset: PeriodPresetId): string {
  return PERIOD_PRESET_LABELS[preset]
}

function presetHint(preset: PeriodPresetId, range: MarketMoneyRange | null): string {
  if (!range) return '—'
  switch (preset) {
    case 'last_month':
      return MONTH_NAMES[Number(range.from.slice(5, 7)) - 1].toLocaleLowerCase('ru-RU')
    case 'quarter': {
      const first = MONTH_SHORT[Number(range.from.slice(5, 7)) - 1]
      const last = MONTH_SHORT[Number(range.to.slice(5, 7)) - 1]
      return first === last ? first : `${first}–${last}`
    }
    case 'all_time':
      return `с ${formatDayMonth(range.from)}`
    default:
      return formatPeriod(range.from, range.to)
  }
}

function dayLabel(isoDate: string): string {
  return `${Number(isoDate.slice(8, 10))} ${MONTH_NAMES_GENITIVE[Number(isoDate.slice(5, 7)) - 1]} ${isoDate.slice(0, 4)}`
}

function orderedRange(first: string, second: string): MarketMoneyRange {
  return first <= second ? { from: first, to: second } : { from: second, to: first }
}

interface Draft {
  range: MarketMoneyRange
  preset: PeriodPresetId | null
}

interface PopoverPlacement {
  side: 'left' | 'right'
  width: number
}

function MonthGrid({ monthKey, draft, today, onPick, nav }: {
  monthKey: string
  draft: MarketMoneyRange
  today: string
  onPick: (isoDate: string) => void
  nav: ReactNode
}) {
  const lead = (new Date(`${monthKey}-01T00:00:00Z`).getUTCDay() + 6) % 7
  const days = Array.from({ length: daysInMonth(monthKey) }, (_, index) => `${monthKey}-${String(index + 1).padStart(2, '0')}`)
  return (
    <div className="flex flex-1 flex-col gap-2">
      <div className="flex items-center justify-between">
        <span className="text-[15px] font-semibold">{MONTH_NAMES[Number(monthKey.slice(5, 7)) - 1]} {monthKey.slice(0, 4)}</span>
        {nav}
      </div>
      <div className="grid grid-cols-7 text-center text-[11px] text-ink-faint">
        {WEEKDAYS.map((weekday) => <span key={weekday}>{weekday}</span>)}
      </div>
      <div className="grid grid-cols-7 gap-y-1">
        {Array.from({ length: lead }, (_, index) => <span key={`lead-${index}`} aria-hidden="true" />)}
        {days.map((isoDate) => {
          const inRange = isoDate >= draft.from && isoDate <= draft.to
          const endpoint = isoDate === draft.from || isoDate === draft.to
          const future = isoDate > today
          return (
            <button
              key={isoDate}
              type="button"
              disabled={future}
              aria-label={dayLabel(isoDate)}
              aria-pressed={endpoint}
              onClick={() => onPick(isoDate)}
              className={`flex h-9 items-center justify-center disabled:cursor-default ${inRange ? 'bg-bone' : ''}`}
            >
              <span
                className={`tab-nums h-6 w-7 rounded-full text-center text-[13px] leading-6 ${endpoint ? 'bg-ink font-semibold text-ivory' : future ? 'text-ink-faint' : 'text-ink'} ${isoDate === today ? 'font-semibold shadow-[inset_0_0_0_1.5px_var(--color-ink)]' : ''}`}
              >
                {Number(isoDate.slice(8, 10))}
              </span>
            </button>
          )
        })}
      </div>
    </div>
  )
}

export function AnalyticsPeriodPicker({ value, firstOrderDate, today, onChange }: AnalyticsPeriodPickerProps) {
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState<Draft>({ range: value.range, preset: value.preset })
  const [anchor, setAnchor] = useState<string | null>(null)
  const [leftMonth, setLeftMonth] = useState(value.range.to.slice(0, 7))
  const [popoverPlacement, setPopoverPlacement] = useState<PopoverPlacement>({ side: 'left', width: 952 })
  const rootRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const dialogRef = useRef<HTMLDivElement>(null)
  const todayMonth = today.slice(0, 7)

  useLayoutEffect(() => {
    if (!open) return undefined
    const updatePlacement = () => {
      const trigger = triggerRef.current?.getBoundingClientRect()
      if (!trigger) return
      const container = rootRef.current?.closest<HTMLElement>('[data-pull-refresh-root]')?.getBoundingClientRect()
      const fallbackWidth = window.innerWidth
      const containerLeft = container && container.width > 0 ? Math.max(12, container.left) : 12
      const containerRight = container && container.width > 0 ? Math.min(fallbackWidth - 12, container.right) : fallbackWidth - 12
      const maxWidth = Math.max(0, Math.min(952, containerRight - containerLeft))
      const leftSpace = Math.max(0, trigger.right - containerLeft - 12)
      const rightSpace = Math.max(0, containerRight - trigger.left - 12)
      const side = rightSpace >= maxWidth || rightSpace >= leftSpace ? 'left' : 'right'
      const width = Math.min(maxWidth, side === 'left' ? rightSpace : leftSpace)
      setPopoverPlacement({ side, width })
    }
    updatePlacement()
    window.addEventListener('resize', updatePlacement)
    return () => window.removeEventListener('resize', updatePlacement)
  }, [open])

  useEffect(() => {
    if (!open) return
    dialogRef.current?.focus()
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      setOpen(false)
      triggerRef.current?.focus()
    }
    const onMouseDown = (event: MouseEvent) => {
      if (event.target instanceof Node && rootRef.current?.contains(event.target)) return
      setOpen(false)
    }
    window.addEventListener('keydown', onKeyDown)
    document.addEventListener('mousedown', onMouseDown)
    return () => {
      window.removeEventListener('keydown', onKeyDown)
      document.removeEventListener('mousedown', onMouseDown)
    }
  }, [open])

  function showMonthsFor(range: MarketMoneyRange) {
    const rangeMonth = range.to.slice(0, 7)
    setLeftMonth(rangeMonth < todayMonth ? rangeMonth : shiftMonth(todayMonth, -1))
  }

  function toggleOpen() {
    if (open) {
      setOpen(false)
      return
    }
    setDraft({ range: value.range, preset: value.preset })
    setAnchor(null)
    showMonthsFor(value.range)
    setOpen(true)
  }

  function choosePreset(preset: PeriodPresetId) {
    const range = presetRange(preset, today, firstOrderDate)
    if (!range) return
    setDraft({ range, preset })
    setAnchor(null)
    showMonthsFor(range)
  }

  function pickDay(isoDate: string) {
    if (anchor === null) {
      setDraft({ range: { from: isoDate, to: isoDate }, preset: null })
      setAnchor(isoDate)
      return
    }
    setDraft({ range: orderedRange(anchor, isoDate), preset: null })
    setAnchor(null)
  }

  function reset() {
    choosePreset(DEFAULT_PERIOD_PRESET)
  }

  function apply() {
    onChange({ range: draft.range, preset: draft.preset })
    setOpen(false)
    triggerRef.current?.focus()
  }

  const rightMonth = shiftMonth(leftMonth, 1)
  const triggerText = formatRangeWithYear(value.range.from, value.range.to)

  return (
    <div ref={rootRef} className="relative flex h-9 min-w-0 max-w-full items-center">
      <button
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`Период аналитики: ${triggerText}${value.preset ? `, ${presetLabel(value.preset).toLocaleLowerCase('ru-RU')}` : ''}`}
        ref={triggerRef}
        onClick={toggleOpen}
        data-testid="period-trigger"
        className="flex h-9 min-w-0 max-w-full items-center gap-2.5 rounded-[4px] border border-ink bg-paper px-3 text-[13px] text-ink"
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true" className="shrink-0"><rect x="2" y="3" width="12" height="11" rx="1" /><path d="M2 6.5h12M5.5 1.5v3M10.5 1.5v3" /></svg>
        <span className="tab-nums min-w-0 truncate font-semibold">{triggerText}</span>
        {value.preset && <span data-testid="period-pill" className="shrink-0 whitespace-nowrap rounded-full bg-bone px-2 py-0.5 text-[11px] font-semibold text-gold-deep">{presetLabel(value.preset).toLocaleLowerCase('ru-RU')}</span>}
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true" className={`shrink-0 ${open ? 'rotate-180' : ''}`}><path d="M3 4.5 6 7.5 9 4.5" /></svg>
      </button>

      {open && (
        <div
          ref={dialogRef}
          tabIndex={-1}
          role="dialog"
          aria-label="Период аналитики"
          data-testid="period-popover"
          className={`absolute top-full z-40 mt-2 flex max-h-[80vh] flex-col overflow-y-auto border border-rule-strong bg-paper shadow-[0_12px_32px_rgba(20,17,13,0.10)] ${popoverPlacement.side === 'left' ? 'left-0' : 'right-0'}`}
          style={{ width: `${popoverPlacement.width}px` }}
        >
          <div className="flex flex-col gap-1 border-b border-rule px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-col gap-[3px]">
              <div className="eyebrow">Период аналитики</div>
              <div className="text-[13px] text-ink-muted">Действует на всю страницу</div>
            </div>
            <span className="text-[11px] text-ink-faint">Продажи — по дате заказа, выплаты — по дате поступления</span>
          </div>

          <div className="flex min-h-0 flex-1 flex-col md:flex-row">
            <div className="flex flex-col gap-0.5 border-b border-rule px-2.5 py-3.5 md:w-[250px] md:shrink-0 md:border-b-0 md:border-r">
              {PERIOD_PRESET_IDS.map((preset) => {
                const range = presetRange(preset, today, firstOrderDate)
                const active = draft.preset === preset
                return (
                  <button
                    key={preset}
                    type="button"
                    disabled={!range}
                    aria-pressed={active}
                    onClick={() => choosePreset(preset)}
                    className={`flex items-center justify-between border-l-2 px-2.5 py-2 text-left text-[13px] text-ink transition-colors hover:bg-bone/60 disabled:opacity-50 ${active ? 'border-gold bg-bone font-semibold' : 'border-transparent'}`}
                  >
                    <span>{PERIOD_PRESET_LABELS[preset]}</span>
                    <span className="tab-nums text-[11px] font-normal text-ink-faint">{presetHint(preset, range)}</span>
                  </button>
                )
              })}
            </div>

            <div className="flex flex-1 flex-col gap-3.5 px-5 py-4">
              <div className="flex flex-col gap-7 sm:flex-row">
                <MonthGrid
                  monthKey={leftMonth}
                  draft={draft.range}
                  today={today}
                  onPick={pickDay}
                  nav={<button type="button" aria-label="Предыдущий месяц" onClick={() => setLeftMonth(shiftMonth(leftMonth, -1))} className="px-1 text-[12px] text-ink-faint hover:text-ink">←</button>}
                />
                <MonthGrid
                  monthKey={rightMonth}
                  draft={draft.range}
                  today={today}
                  onPick={pickDay}
                  nav={<button type="button" aria-label="Следующий месяц" disabled={rightMonth >= todayMonth} onClick={() => setLeftMonth(shiftMonth(leftMonth, 1))} className="px-1 text-[12px] text-ink-faint hover:text-ink disabled:opacity-30">→</button>}
                />
              </div>
              <div className="flex flex-wrap gap-[18px] text-[11px] text-ink-muted">
                <span className="flex items-center gap-1.5"><span className="size-3 bg-bone" />выбранный период</span>
                <span className="flex items-center gap-1.5"><span className="size-3 rounded-full shadow-[inset_0_0_0_1.5px_var(--color-ink)]" />сегодня</span>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-3 border-t border-rule bg-ivory px-5 py-3.5 sm:flex-row sm:items-center sm:justify-between">
            <span className="tab-nums text-[15px] font-semibold">
              {formatRangeWithYear(draft.range.from, draft.range.to)} · {pluralRu(inclusiveDays(draft.range.from, draft.range.to), ['день', 'дня', 'дней'])}
            </span>
            <div className="flex gap-2">
              <button type="button" onClick={reset} className="rounded-[4px] border border-rule-strong px-4 py-2 text-[13px] hover:bg-bone">Сбросить</button>
              <button type="button" onClick={apply} className="rounded-[4px] bg-ink px-4 py-2 text-[13px] font-semibold text-ivory hover:bg-onyx-2">Применить</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
